import type { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect, useRef } from 'react';

import type { ISuggestionAnnotation } from '../annotationCommands/SuggestionAnnotation';
import { SUGGESTION_SUBJECT } from '../annotationCommands/consts';
import { getAnnotationViewportRect } from '../annotationCommands/suggestionUtils';

const isPointInRect = (
  x: number,
  y: number,
  rect: { left: number; top: number; width: number; height: number },
): boolean =>
  x >= rect.left &&
  x <= rect.left + rect.width &&
  y >= rect.top &&
  y <= rect.top + rect.height;

const ATTR = 'data-over-extraction';

export const useExtractionHoverCursor = ({
  instance,
}: {
  instance: WebViewerInstance | null;
}) => {
  const rafRef = useRef<number | null>(null);
  const lastOverExtractionRef = useRef<boolean>(false);

  useEffect(() => {
    if (!instance) return;

    const documentViewer = instance.Core.documentViewer;
    const annotationManager = instance.Core.annotationManager;
    const scrollViewElement = documentViewer.getScrollViewElement();

    const handleMouseMove = (e: Event) => {
      if (rafRef.current !== null || !(e instanceof MouseEvent)) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const { clientX, clientY } = e;
        const annotations = annotationManager.getAnnotationsList();
        const visible = annotations.filter(
          (a) => a.Subject === SUGGESTION_SUBJECT && !a.Hidden,
        );
        let isOverNonRegionExtraction = false;
        for (const annot of visible) {
          const suggestion = annot as ISuggestionAnnotation;
          if (suggestion.displayType === 'region') continue;
          const rect = getAnnotationViewportRect(annot, instance);
          if (rect && isPointInRect(clientX, clientY, rect)) {
            isOverNonRegionExtraction = true;
            break;
          }
        }
        if (lastOverExtractionRef.current !== isOverNonRegionExtraction) {
          lastOverExtractionRef.current = isOverNonRegionExtraction;
          if (isOverNonRegionExtraction) {
            scrollViewElement.setAttribute(ATTR, 'true');
          } else {
            scrollViewElement.removeAttribute(ATTR);
          }
        }
      });
    };

    const handleMouseLeave = () => {
      if (lastOverExtractionRef.current) {
        lastOverExtractionRef.current = false;
        scrollViewElement.removeAttribute(ATTR);
      }
    };

    scrollViewElement.addEventListener('mousemove', handleMouseMove, {
      passive: true,
    });
    scrollViewElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      scrollViewElement.removeEventListener('mousemove', handleMouseMove);
      scrollViewElement.removeEventListener('mouseleave', handleMouseLeave);

      // Clean up any pending animation frames
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

      // Remove the attribute and reset the state
      scrollViewElement.removeAttribute(ATTR);
      lastOverExtractionRef.current = false;
    };
  }, [instance]);
};
